/**
 * AgentModelFields — the ONE atomic "Agent + model + credential" form body.
 *
 * This is the single source of truth for the FIELDS (agent:model picker +
 * credential input(s) + help text + optional "save as project default"
 * toggle). It is presentational + fully controlled: the parent owns the state
 * and the surrounding chrome (modal Save/Cancel, settings-card Clear/Save).
 *
 * Used by:
 *   - AgentModelModal (modal chrome) — pipeline + app + deploy key capture.
 *   - ProjectSettings → AI Settings (inline card).
 * Both render THIS, so they look + behave identically.
 *
 * Credentials may arrive MASKED (sk-a...wxyz). A value still containing '...'
 * is the untouched server seed — focusing the field clears it so the user can
 * type a fresh key, and callers must treat a still-masked value as "keep".
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Field from '../../atoms/Field/Field';
import AgentModelSelect from '../../atoms/AgentModelSelect/AgentModelSelect';
import { IonToggle } from '@ionic/react';
import { parseAgentOption } from '../../../utils/agent-options';
import './AgentModelFields.css';

// Credential format gates. claude api03 = standard API key, oat01 = Pro/Max
// OAuth token — both are valid in the single Claude key field.
export const CLAUDE_KEY_RE = /^sk-ant-(oat01|api03)-/;
export const OPENAI_KEY_RE = /^sk-/;
export const GEMINI_KEY_RE = /^AIza/;
export const CLAUDE_OAUTH_RE = /^sk-ant-oat01-/;

export const VENDOR_KEY_HINT = {
  claude: 'sk-ant-…',
  codex: 'sk-…',
  gemini: 'AIza…',
};
export const VENDOR_KEY_LABEL = {
  claude: 'API key / OAuth token',
  codex: 'OpenAI API key',
  gemini: 'Google API key',
};

// A value still carrying the '...' mask is the untouched server seed.
export const isMask = (v) => typeof v === 'string' && v.includes('...');

// The single Claude credential field accepts either shape. Route by prefix:
// sk-ant-oat01-… is a Pro/Max OAuth token (→ aiOAuthToken / CLAUDE_CODE_OAUTH_TOKEN);
// everything else (sk-ant-api03-…) is an API key (→ aiApiKey / ANTHROPIC_API_KEY).
export const isClaudeOAuthToken = (v) => typeof v === 'string' && /^sk-ant-oat01-/.test(v.trim());

/**
 * Validate one credential field for a vendor.
 * @returns {string} '' when valid, else a SHORT inline error message.
 */
export function validateToken({ vendor, value, maskedSeed, required = true }) {
  const s = typeof value === 'string' ? value : '';
  // Field still showing the masked stored key (untouched) → keep it, valid.
  if (isMask(s)) return '';
  // Empty → REQUIRED. Note we do NOT fall back to maskedSeed here: an empty
  // field means the user cleared the stored key, so they must enter a new one
  // (clearing must not silently deploy on the project key). "Keep stored" is
  // the isMask() case above, not an empty field. (`maskedSeed` is intentionally
  // unused now — kept in the signature for call-site compatibility.)
  if (s.trim() === '') {
    return required ? 'API key required' : '';
  }
  const v = s.trim();
  if (vendor === 'claude') {
    if (!CLAUDE_KEY_RE.test(v)) return 'Key must start with sk-ant-…';
  } else if (vendor === 'gemini') {
    if (!GEMINI_KEY_RE.test(v)) return 'Key must start with AIza…';
  } else {
    if (!OPENAI_KEY_RE.test(v)) return 'Key must start with sk-…';
  }
  return '';
}

// Per-vendor help copy shown under the single (non-claude) key field.
function VendorKeyHelp({ vendor }) {
  if (vendor === 'codex') {
    return (
      <>OpenAI API key (<code>sk-…</code>). From{' '}
        <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">platform.openai.com</a>.{' '}
        Encrypted and stored securely.</>
    );
  }
  if (vendor === 'gemini') {
    return (
      <>Google AI API key (<code>AIza…</code>). From{' '}
        <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer">aistudio.google.com</a>.{' '}
        Encrypted and stored securely.</>
    );
  }
  return (
    <>API key for the selected agent. Encrypted and stored securely.</>
  );
}
VendorKeyHelp.propTypes = { vendor: PropTypes.string };

export default function AgentModelFields({
  agentOptions = [],
  value = '',
  onValueChange,
  apiKey = '',
  onApiKeyChange,
  submitted = false,
  serverKeyError = '',
  disabled = false,
  idPrefix = 'agent-model',
  showSaveToProject = false,
  saveToProject = true,
  onSaveToProjectChange,
  saveToProjectLabel = 'Save as project default',
}) {
  // Credential input starts readOnly so the browser can't autofill a saved
  // password into it; it unlocks on first focus (by then autofill passed).
  const [credUnlocked, setCredUnlocked] = useState(false);

  const vendor = parseAgentOption(value).aiAgent;
  const isClaude = vendor === 'claude';
  const needsKey = Boolean(vendor);

  const apiMasked = isMask(apiKey);

  // ONE credential field everywhere — it accepts EITHER an API key
  // (sk-ant-api03-…) or a Pro/Max OAuth token (sk-ant-oat01-…). The save layer
  // routes it to aiApiKey vs aiOAuthToken by prefix (see classifyClaudeToken).
  const tokenError = needsKey
    ? validateToken({ vendor, value: apiKey, maskedSeed: '', required: true })
    : '';
  const shownKeyErr = serverKeyError || (submitted ? tokenError : '');

  return (
    <div className="agent-model-fields">
      <div className="agent-model-field">
        <label className="apps-modal-field-label" htmlFor={`${idPrefix}-pick`}>Model &amp; engine</label>
        <AgentModelSelect
          id={`${idPrefix}-pick`}
          options={agentOptions}
          value={value}
          onChange={(v) => onValueChange?.(v)}
          className="mkt-field mkt-field--select"
          ariaLabel="Model and engine"
        />
      </div>

      {needsKey && (
        <div className="agent-model-field">
          <label className="apps-modal-field-label" htmlFor={`${idPrefix}-key`}>
            {VENDOR_KEY_LABEL[vendor] || 'API key'}
          </label>
          <Field
            as="input"
            id={`${idPrefix}-key`}
            type="text"
            name="zibby-agent-token"
            value={apiKey}
            placeholder={VENDOR_KEY_HINT[vendor] || 'API key'}
            onChange={(e) => onApiKeyChange?.(e.target.value)}
            readOnly={!credUnlocked}
            onFocus={() => { setCredUnlocked(true); if (apiMasked) onApiKeyChange?.(''); }}
            autoComplete="new-password"
            data-1p-ignore="true"
            data-lpignore="true"
            spellCheck={false}
            disabled={disabled}
            status={shownKeyErr ? 'error' : null}
            aria-invalid={shownKeyErr ? true : undefined}
          />
          {shownKeyErr ? (
            <p className="agent-model-field-error">{shownKeyErr}</p>
          ) : (
            <p className="agent-model-field-hint">
              {apiMasked && <span className="agent-model-saved-ok">✓ Saved. </span>}
              {isClaude ? (
                <>Anthropic API key (<code>sk-ant-api03-…</code>). Get one at{' '}
                  <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer">console.anthropic.com</a>.</>
              ) : (
                <VendorKeyHelp vendor={vendor} />
              )}
            </p>
          )}
        </div>
      )}

      {showSaveToProject && needsKey && (
        <div className="agent-model-save-project">
          <IonToggle
            checked={saveToProject}
            onIonChange={(e) => onSaveToProjectChange?.(e.detail.checked)}
            disabled={disabled}
            aria-label={saveToProjectLabel}
          />
          <span>{saveToProjectLabel}</span>
        </div>
      )}
    </div>
  );
}

AgentModelFields.propTypes = {
  agentOptions: PropTypes.array,
  value: PropTypes.string,
  onValueChange: PropTypes.func,
  apiKey: PropTypes.string,
  onApiKeyChange: PropTypes.func,
  submitted: PropTypes.bool,
  serverKeyError: PropTypes.string,
  disabled: PropTypes.bool,
  idPrefix: PropTypes.string,
  showSaveToProject: PropTypes.bool,
  saveToProject: PropTypes.bool,
  onSaveToProjectChange: PropTypes.func,
  saveToProjectLabel: PropTypes.string,
};
